<template>
    <div @blur="onCodeContainerBlur" tabindex="0">
        <modal :name="editorId"
               width="80%"
               height="98%"
               @before-close="isExpanded = false"
               clickToClose>
            <div class="row mx-0 h-100">
                <div class="col-4 h-100 border-left px-0">
                    <portal-target :name="'EXPANDED-EDITOR-MENU' + editorId" class="h-100"/>
                </div>
                <div class="col-8 px-0 h-100">
                    <portal-target :name="'EXPANDED-EDITOR' + editorId" class="h-100"/>
                </div>
            </div>
        </modal>

        <portal-target
            :name="'COLLAPSED-EDITOR' + editorId"
            class="map-param-code-editor-container border"
            :class="{ 'highlighted-param': showMenu }"/>

        <portal :to="(isExpanded ? 'EXPANDED-EDITOR' : 'COLLAPSED-EDITOR') + editorId">
            <AppCodeEditor
                ref="editor"
                :id="editorId"
                theme="default"
                mimeType="text/javascript"
                :value="editorValue"
                :disabled="disabled"
                withAnyWordHints
                :customHints="customHints"
                @input="onChange"
                @focus="onFocus"
                :codemirrorOptions="{lineWrapping: false, lint: true, lineNumbers: true}"
                class="h-100">
                <template slot="bottom-right-actions">
                    <button v-tooltip="isExpanded ? 'Collapse' : 'Expand'"
                            @click="toggleViewMode"
                            class="btn btn-sm btn-primary footer-btn ml-2">
                        <span v-show="isExpanded">
                            <i class="fa fa-compress-alt"></i>
                        </span>
                        <span v-show="!isExpanded">
                            <i class="fa fa-expand-arrows"></i>
                        </span>
                    </button>
                </template>
            </AppCodeEditor>
        </portal>

        <portal :to="isExpanded ? 'EXPANDED-EDITOR-MENU' + editorId : 'APP_PARAM_MAP_MENU'">
            <AppModuleMappableParameterMenu
                v-if="block && (isExpanded || showMenu)"
                :id="`map-param-menu-${id}`"
                :key="`map-param-menu-${id}`"
                @close="onMenuClose"
                @select="onMenuItemSelect"
                :block="block"
                objectRefOnly
                :withComplexExpressionBtn="false"
                :withCloseBtn="!isExpanded"
                :disabled="disabled"
                :class="{
                    'map-param-menu': !isExpanded && showMenu,
                    'map-param-menu-expanded border-right': isExpanded
                }"/>
        </portal>
    </div>
</template>

<script>
import AppCodeEditor from '../../../shared/AppCodeEditor'
import AppModuleMappableParameterMenu
    from '../../../../views/workflow/mappable-params/menu/AppModuleMappableParameterMenu'
import {APP_MAP_PARAM_OBJECT} from '../../../../views/workflow/mappable-params/menu/app-module-mappable-param-menu'
import {mapGetters} from 'vuex'

export default {
    name: 'AppMapParamCodeEditor',
    components: {AppModuleMappableParameterMenu, AppCodeEditor},
    props: {
        id: {
            type: String,
            required: true
        },
        value: {
            required: true,
            default: ''
        },
        block: {
            // type: Object,
            required: true
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            editorValue: '',
            isExpanded: false,
            showMenu: false
        }
    },
    computed: {
        ...mapGetters([
            'getBlockIncomingObjects'
        ]),

        editorId() {
            return `code-editor-${this.id}`
        },

        editor() {
            return this.$refs.editor
        },

        customHints() {
            if (!this.block) {
                return []
            }
            return this.getBlockIncomingObjects(this.block.id)
                .reduce((map, obj) => {
                    map.push({
                        text: obj.id,
                        displayText: obj.label
                    })
                    map = map.concat(this.getObjectDataHints(obj.data, obj.id, obj.label))
                    return map
                }, [])
        }
    },
    methods: {
        onChange(value) {
            this.editorValue = value
            const serverAcceptedValueFormat = value
                .replace(/,/g, ' , ')
                .replace(/\(/g, '( ')
                .replace(/\)/g, ' )')
                .replace(/[\n\r\s]+/g, ' ')
                .trim()
            this.$emit('change', serverAcceptedValueFormat)
        },

        onMenuClose() {
            this.showMenu = false
        },

        onCodeContainerBlur() {
            if (this.isExpanded) {
                return
            }
            this.showMenu = false
        },

        onFocus() {
            this.showMenu = true
        },

        onMenuItemSelect({type, data}) {
            if (this.disabled) {
                return
            }
            if (type === APP_MAP_PARAM_OBJECT) {
                let ref = data.workflowObject.id
                if (Array.isArray(data.fieldPath)) {
                    ref += '.' + data.fieldPath.join('.')
                }
                this.$refs.editor.insertTextAtCursorPosition(ref)
            }
        },

        async toggleViewMode() {
            this.isExpanded = !this.isExpanded
            if (this.isExpanded) {
                this.$modal.show(this.editorId)
            } else {
                this.$modal.hide(this.editorId)
            }
        },

        getObjectDataHints(data = [], textPrefix, displayTextPrefix) {
            let hints = []
            if (!_.isArray(data)) {
                return []
            }
            let text, displayText
            data.forEach(param => {
                text = `${textPrefix}.${param.name}`
                displayText = `${displayTextPrefix}.${param.label}`
                hints.push({text, displayText})
                if (_.isArray(param.spec)) {
                    hints = hints.concat(this.getObjectDataHints(param.spec, text, displayText))
                }
            })
            return hints
        }
    },
    created() {
        // NOTE: Code editor supports only plain JS syntax,
        // so we remove all '{{' '}}' for object/property references,
        // and wrap with it on value save
        // (=> it is not possible to write complex expressions in code editor)
        if (typeof this.value === 'string') {
            this.editorValue = this.value
                .replace(/{{/g, '')
                .replace(/}}/g, '')
                .trim()
        }
    }
}
</script>

<style lang="scss" scoped>
    .map-param-code-editor-container {
        height: 140px;
        width: 100%;
    }

    .map-param-menu-expanded {
        height: 100%;
        overflow-y: auto;
    }
</style>
